import React, { useState, useMemo, useRef, useEffect } from "react";
import Page from "./UI/Page";
import {
  Grid,
  GridItem,
  Text,
  Icon,
  Box,
  Progress,
  VStack,
  HStack,
  Heading,
  Badge,
  Card,
  CardBody,
  Button,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { CheckCircleIcon, StarIcon } from "@chakra-ui/icons";

// ─── Data ────────────────────────────────────────────────────
const milestones = [
  { amount: 1, label: "First Steps to Freedom", emoji: "👣", date: "2025-05-16", futureUse: "Symbolic start — builds consistent investing habit", category: "Baby" },
  { amount: 2, label: "Small Treats", emoji: "🍬", date: "2025-07-14", futureUse: "Monthly comic book allowance or app store credit", category: "Baby" },
  { amount: 3, label: "Baby Essentials", emoji: "🍼", date: "2025-08-09", futureUse: "Covers a monthly streaming service", category: "Baby" },
  { amount: 4, label: "Snack Smart", emoji: "🍎", date: "2026-02-13", futureUse: "Weekly healthy snacks", category: "Baby" },
  { amount: 5, label: "Toys & Books Fund", emoji: "📚🎲", date: "2026-04-15", futureUse: "Monthly book budget or game add-ons", category: "Baby" },
  { amount: 6, label: "Mini Subscriptions", emoji: "📱", date: "2026-06-16", futureUse: "Educational app subscription", category: "Baby" },
  { amount: 7.5, label: "Daily Outings", emoji: "🚸", futureUse: "Public transport allowance for school", category: "Baby" },
  { amount: 8, label: "Weekend Fun", emoji: "🎠", futureUse: "Short weekend activities", category: "Baby" },
  { amount: 10, label: "Diapers & Milk Covered", emoji: "🍼", date: "2024-07-05", futureUse: "Mobile phone credit or snack budget", category: "Baby" },
  { amount: 12.5, label: "Learning Boost", emoji: "🔤", futureUse: "Monthly books/educational games", category: "Baby" },
  { amount: 15, label: "Educational Toys", emoji: "🧸", futureUse: "Monthly gym/fitness contribution", category: "Baby" },
  { amount: 17.5, label: "Healthy Habits", emoji: "🥗", futureUse: "Nutritious school lunches", category: "Baby" },
  { amount: 20, label: "Medical Checkups", emoji: "🏥", futureUse: "Savings for computer accessories/upgrades", category: "School" },
  { amount: 22.5, label: "Hobby Starter", emoji: "🧪", futureUse: "STEM kits and experiments", category: "School" },
  { amount: 25, label: "Kindergarten Covered", emoji: "🎒", date: "2024-08-15", futureUse: "Weekly coffee fund with friends", category: "School" },
  { amount: 27.5, label: "Local Adventures", emoji: "🗺️", futureUse: "Monthly museum/field trips nearby", category: "School" },
  { amount: 30, label: "Preschool Prep", emoji: "✏️", futureUse: "Monthly fuel for bike/scooter", category: "School" },
  { amount: 32.5, label: "Bike Safety Kit", emoji: "🚴", futureUse: "Helmet, lights, lock maintenance", category: "School" },
  { amount: 35, label: "After-School Light", emoji: "🧮", futureUse: "Math/reading club fees", category: "School" },
  { amount: 37.5, label: "Library Plus", emoji: "📖", futureUse: "Library events & makerspace passes", category: "School" },
  { amount: 40, label: "Learning Tools", emoji: "🧩", futureUse: "Music/movie platform subscriptions", category: "School" },
  { amount: 42.5, label: "Field Trip Extras", emoji: "🧃", futureUse: "Snacks & souvenirs on trips", category: "School" },
  { amount: 45, label: "Activity Pass", emoji: "🎟️", futureUse: "Pool/workshop monthly access", category: "School" },
  { amount: 47.5, label: "Sports Tryouts", emoji: "🏅", futureUse: "One-off club/tryout fees", category: "School" },
  { amount: 50, label: "School Support Fund", emoji: "🏫", futureUse: "Car insurance contribution", category: "School" },
  { amount: 52.5, label: "Coding Sprouts", emoji: "💻", futureUse: "Intro coding mini-course", category: "School" },
  { amount: 55, label: "Skills Upgrade", emoji: "⌨️", futureUse: "Digital skills (typing/coding junior)", category: "School" },
  { amount: 57.5, label: "Language Sprout", emoji: "🗣️", futureUse: "Language app subscription", category: "School" },
  { amount: 60, label: "Community Classes", emoji: "🧑‍🏫", futureUse: "Community center workshops", category: "School" },
  { amount: 62.5, label: "Music Rental", emoji: "🎻", futureUse: "Starter instrument rental", category: "School" },
  { amount: 65, label: "Sport & Safety", emoji: "🛡️", futureUse: "Sports gear + protections", category: "School" },
  { amount: 67.5, label: "PE Upgrade", emoji: "👟", futureUse: "Running shoes replacement", category: "School" },
  { amount: 70, label: "Science Fair Kit", emoji: "🧬", futureUse: "Materials for school projects", category: "School" },
  { amount: 72.5, label: "Art Chest", emoji: "🎨", futureUse: "Quality paints & canvas", category: "School" },
  { amount: 75, label: "School Trips", emoji: "🚌", futureUse: "Monthly savings for travel experiences", category: "School" },
  { amount: 77.5, label: "Swim Progress", emoji: "🏊", futureUse: "Extra swimming lessons", category: "School" },
  { amount: 80, label: "Robotics Club Fee", emoji: "🤖", futureUse: "Robotics/engineering club dues", category: "School" },
  { amount: 82.5, label: "Chess & Logic", emoji: "♟️", futureUse: "Chess club & logic books", category: "School" },
  { amount: 85, label: "Weekend Course", emoji: "🗓️", futureUse: "Weekend workshop (music/art/robotics)", category: "School" },
  { amount: 87.5, label: "Exam Prep Lite", emoji: "📒", futureUse: "Practice tests & workbooks", category: "School" },
  { amount: 90, label: "Junior Festival Pass", emoji: "🎫", futureUse: "Local kids festival weekend", category: "School" },
  { amount: 95, label: "Backup Tech", emoji: "🔌", futureUse: "Power bank & earbuds", category: "School" },
  { amount: 100, label: "Extracurricular Activities", emoji: "🎨⚽", futureUse: "Monthly club membership fees", category: "School" },
  { amount: 110, label: "Online Course Credit", emoji: "🧑‍💻", futureUse: "One structured online course", category: "School" },
  { amount: 125, label: "Bike Upgrade", emoji: "🚲", futureUse: "Better components or tune-ups", category: "School" },
  { amount: 150, label: "Sports Equipment", emoji: "⚽🏀", futureUse: "Team sports participation costs", category: "Teen" },
  { amount: 175, label: "Tournament Travel", emoji: "🧳", futureUse: "Regional competitions travel", category: "Teen" },
  { amount: 200, label: "First Investment Account", emoji: "📈", futureUse: "Stock market learning portfolio", category: "Teen" },
  { amount: 225, label: "Certification Starter", emoji: "🏅", futureUse: "Exam fees (languages/IT)", category: "Teen" },
  { amount: 250, label: "Tech Upgrade Fund", emoji: "💻", futureUse: "Laptop/tablet replacement budget", category: "Teen" },
  { amount: 275, label: "Creator Kit", emoji: "🎥", futureUse: "Mic, tripod, lighting basics", category: "Teen" },
  { amount: 300, label: "Summer Camps", emoji: "🏕️", futureUse: "Outdoor adventure experiences", category: "Teen" },
  { amount: 325, label: "Language Exam Fund", emoji: "📝", futureUse: "B1/B2 preparation & fees", category: "Teen" },
  { amount: 350, label: "Music Mastery", emoji: "🎹", futureUse: "Instrument lessons & maintenance", category: "Teen" },
  { amount: 375, label: "Band/Orchestra Season", emoji: "🎼", futureUse: "Ensemble fees & concerts", category: "Teen" },
  { amount: 400, label: "Tutoring Fund", emoji: "📖", futureUse: "Exam/University prep courses", category: "Teen" },
  { amount: 425, label: "Mentorship Sessions", emoji: "🤝", futureUse: "1-to-1 coaching/mentoring", category: "Teen" },
  { amount: 450, label: "Creative Arts", emoji: "🎭", futureUse: "Art supplies or theater tickets", category: "Young Adult" },
  { amount: 475, label: "Creative Portfolio", emoji: "🗂️", futureUse: "Portfolio website & printing", category: "Young Adult" },
  { amount: 500, label: "University Covered", emoji: "🎓", futureUse: "Textbooks and study materials", category: "Young Adult" },
  { amount: 525, label: "Textbook Cushion", emoji: "📚", futureUse: "Supplemental readings & labs", category: "Young Adult" },
  { amount: 575, label: "Campus Transport", emoji: "🚌", futureUse: "Monthly passes or bike repairs", category: "Young Adult" },
  { amount: 600, label: "Driver's License Fund", emoji: "🚗", futureUse: "Driving lessons and license fees", category: "Young Adult" },
  { amount: 650, label: "Driving Practice Fuel", emoji: "⛽", futureUse: "Fuel & parking for practice", category: "Young Adult" },
  { amount: 700, label: "Travel Experience", emoji: "🌍", futureUse: "Backpacking trip foundation", category: "Young Adult" },
  { amount: 725, label: "Study Trip Booster", emoji: "🗺️", futureUse: "Conferences/study visits", category: "Young Adult" },
  { amount: 775, label: "Internship Costs", emoji: "🧾", futureUse: "Commute, meals, attire", category: "Young Adult" },
  { amount: 800, label: "Study Abroad Semester", emoji: "✈️", futureUse: "International living expenses", category: "Young Adult" },
  { amount: 850, label: "Visa & Docs Prep", emoji: "🛂", futureUse: "Visa, translations, insurance", category: "Young Adult" },
  { amount: 900, label: "Startup Seed Money", emoji: "💡", futureUse: "First business venture capital", category: "Young Adult" },
  { amount: 925, label: "Prototype Fund", emoji: "🛠️", futureUse: "MVP parts & hosting", category: "Young Adult" },
  { amount: 1000, label: "Financial Independence", emoji: "🔥", futureUse: "Full basic living expenses", category: "Young Adult" },
];

const CATEGORIES = ["All", "Baby", "School", "Teen", "Young Adult"];

const CATEGORY_COLORS = {
  All: "teal",
  Baby: "pink",
  School: "blue",
  Teen: "purple",
  "Young Adult": "orange",
};

const currentDividends = 6.152;

// The final milestone is the long-term goal used for value-based progress.
const GOAL_AMOUNT = 1000;

// Pulsing ring to draw the eye to the next milestone among the 72 cards.
const pulseRing = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(56, 178, 172, 0.7); }
  70%  { box-shadow: 0 0 0 10px rgba(56, 178, 172, 0); }
  100% { box-shadow: 0 0 0 0 rgba(56, 178, 172, 0); }
`;

// ─── Helpers ─────────────────────────────────────────────────
const getProgress = (target) =>
  Math.round(Math.min((currentDividends / target) * 100, 100));

const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

// ─── Milestone Card ──────────────────────────────────────────
const MilestoneCard = ({ milestone, isNext, cardRef, animateNext }) => {
  const isReached = currentDividends >= milestone.amount;
  const progress = getProgress(milestone.amount);

  return (
    <GridItem
      ref={cardRef}
      p={{ base: 2.5, sm: 3, md: 4 }}
      borderRadius={{ base: "md", md: "lg" }}
      bg={
        isReached
          ? "linear-gradient(145deg, #48BB78 0%, #38A169 100%)"
          : "gray.700"
      }
      color="white"
      textAlign="center"
      minH={{ base: "130px", md: "160px" }}
      display="flex"
      flexDir="column"
      justifyContent="space-between"
      alignItems="center"
      position="relative"
      boxShadow={isReached ? "xl" : isNext ? "0 0 0 2px" : "md"}
      borderWidth={isNext ? "2px" : "0"}
      borderColor={isNext ? "teal.300" : "transparent"}
      transition="all 0.2s"
      animation={isNext && animateNext ? `${pulseRing} 2s infinite` : undefined}
      _hover={{ transform: "translateY(-2px)" }}
      aria-current={isNext ? "step" : undefined}
    >
      {/* Check icon */}
      <Icon
        as={CheckCircleIcon}
        color="white"
        boxSize={{ base: "14px", md: "18px" }}
        position="absolute"
        top={1.5}
        right={1.5}
        opacity={isReached ? 1 : 0.3}
        aria-label={isReached ? "Milestone reached" : "Milestone not yet reached"}
      />

      {/* Main content */}
      <Box w="full">
        <Text
          fontSize={{ base: "xl", md: "2xl" }}
          mb={1}
          role="img"
          aria-label={milestone.label}
        >
          {milestone.emoji}
        </Text>
        <Text
          fontWeight="extrabold"
          fontSize={{ base: "md", md: "lg" }}
          letterSpacing="tight"
          mb={0.5}
        >
          ${milestone.amount}/mo
        </Text>
        <Text
          fontSize={{ base: "xs", md: "sm" }}
          fontWeight="medium"
          mb={1}
          lineHeight="tight"
          noOfLines={2}
        >
          {milestone.label}
        </Text>
      </Box>

      {/* Progress or achievement date */}
      <Box w="full">
        {isReached ? (
          milestone.date && (
            <Text fontSize="2xs" opacity={0.85} fontStyle="italic">
              Achieved: {formatDate(milestone.date)}
            </Text>
          )
        ) : (
          <Box>
            <Text
              fontSize={{ base: "2xs", md: "xs" }}
              fontWeight="bold"
              mb={0.5}
              color="green.200"
            >
              {100 - progress}% TO GO
            </Text>
            <Box
              bg="blackAlpha.300"
              h="2px"
              borderRadius="full"
              overflow="hidden"
            >
              <Box
                w={`${progress}%`}
                bg="green.300"
                h="100%"
                transition="width 0.3s"
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Future use — hide on very small mobile to save space */}
      <Box
        mt={1.5}
        pt={1.5}
        borderTop="1px solid"
        borderColor={isReached ? "whiteAlpha.200" : "blackAlpha.200"}
        w="full"
        display={{ base: "none", sm: "block" }}
      >
        <Text
          fontSize="2xs"
          fontWeight="bold"
          mb={0.5}
          opacity={0.8}
          textTransform="uppercase"
          letterSpacing="wide"
        >
          Future Use
        </Text>
        <Text fontSize="2xs" lineHeight="tight" opacity={0.7} noOfLines={2}>
          {milestone.futureUse}
        </Text>
      </Box>
    </GridItem>
  );
};

// ─── Main Component ──────────────────────────────────────────
const Milestone = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const prefersReducedMotion = usePrefersReducedMotion();
  const nextCardRef = useRef(null);

  const filteredMilestones = useMemo(
    () =>
      selectedCategory === "All"
        ? milestones
        : milestones.filter((m) => m.category === selectedCategory),
    [selectedCategory]
  );

  const nextMilestone = milestones.find((m) => m.amount > currentDividends);
  const reachedCount = milestones.filter(
    (m) => currentDividends >= m.amount
  ).length;
  const overallProgress = Math.round((reachedCount / milestones.length) * 100);

  const nextTarget = nextMilestone?.amount || GOAL_AMOUNT;
  const remaining = (nextTarget - currentDividends).toFixed(2);
  const progressToNext = getProgress(nextTarget);

  // Value-based progress toward the long-term $1000/mo goal (distinct from the
  // count of milestones reached, which weights tiny and huge tiers equally).
  const goalProgress = Math.round(
    Math.min((currentDividends / GOAL_AMOUNT) * 100, 100)
  );

  // Bring the next milestone into view once on mount, and whenever the active
  // filter changes to a category that contains it. Honors reduced-motion.
  useEffect(() => {
    if (!nextCardRef.current) return;
    nextCardRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "center",
    });
  }, [selectedCategory, prefersReducedMotion]);

  const motivation = useMemo(() => {
    const p = overallProgress;
    if (p >= 100) return "🏁 Goal reached — extraordinary commitment!";
    if (p >= 90) return "🔥 You're almost there — phenomenal dedication!";
    if (p >= 75) return "🚀 Amazing progress! You're on track!";
    if (p >= 50) return "💪 Halfway hero — the second half starts now!";
    if (p >= 25) return "🌱 Solid start! Small steps make a big difference.";
    if (p > 0) return "👣 The journey has begun — keep the momentum!";
    return "✨ Ready to take the first step toward growth?";
  }, [overallProgress]);

  // Count per category for filter badges
  const categoryCounts = useMemo(() => {
    const counts = {};
    CATEGORIES.forEach((cat) => {
      if (cat === "All") {
        counts[cat] = milestones.length;
      } else {
        counts[cat] = milestones.filter((m) => m.category === cat).length;
      }
    });
    return counts;
  }, []);

  return (
    <Page>
      <div className="hero" style={{ minHeight: "100vh" }}>
        <Box py={{ base: 4, md: 6 }}>
          <VStack
            spacing={{ base: 4, md: 6 }}
            maxW="1600px"
            mx="auto"
            px={{ base: 2, md: 4 }}
          >
            {/* Header */}
            <VStack spacing={1}>
              <Heading
                size={{ base: "sm", md: "lg" }}
                textAlign="center"
                color="teal.300"
                fontWeight="bold"
              >
                🎯 Dividend Milestones Journey
              </Heading>
              <Text
                fontSize={{ base: "xs", md: "sm" }}
                color="gray.400"
                textAlign="center"
                maxW="2xl"
                px={{ base: 2, md: 0 }}
              >
                Tracking monthly dividend income goals for Mattias' future.
              </Text>
            </VStack>

            {/* Current Status Card */}
            <Card
              bg="rgba(45, 55, 72, 0.8)"
              borderWidth="2px"
              borderColor="teal.500"
              boxShadow="0 0 30px rgba(56, 178, 172, 0.3)"
              maxW="600px"
              w="full"
              backdropFilter="blur(10px)"
            >
              <CardBody p={{ base: 3, md: 5 }}>
                <VStack spacing={{ base: 3, md: 4 }}>
                  <HStack spacing={2}>
                    <Icon
                      as={StarIcon}
                      color="yellow.300"
                      boxSize={{ base: 4, md: 5 }}
                    />
                    <Text
                      fontSize={{ base: "2xs", md: "xs" }}
                      color="teal.200"
                      fontWeight="bold"
                      letterSpacing="wider"
                    >
                      CURRENT MONTHLY INCOME
                    </Text>
                    <Icon
                      as={StarIcon}
                      color="yellow.300"
                      boxSize={{ base: 4, md: 5 }}
                    />
                  </HStack>
                  <Text
                    fontSize={{ base: "3xl", md: "4xl" }}
                    fontWeight="extrabold"
                    color="green.300"
                  >
                    ${currentDividends.toFixed(2)}
                  </Text>

                  {nextMilestone && (
                    <Box w="full">
                      <HStack justify="space-between" mb={2}>
                        <Text
                          fontSize={{ base: "2xs", md: "xs" }}
                          color="gray.300"
                          fontWeight="medium"
                        >
                          Next: {nextMilestone.emoji} ${nextMilestone.amount}
                        </Text>
                        <Badge
                          colorScheme="teal"
                          fontSize={{ base: "2xs", md: "xs" }}
                          px={2}
                          py={0.5}
                        >
                          ${remaining} to go
                        </Badge>
                      </HStack>
                      <Progress
                        value={progressToNext}
                        colorScheme="green"
                        size={{ base: "sm", md: "md" }}
                        borderRadius="full"
                        hasStripe
                        isAnimated
                        bg="blackAlpha.400"
                      />
                    </Box>
                  )}
                </VStack>
              </CardBody>
            </Card>

            {/* Overall Progress */}
            <Card
              bg="gray.800"
              borderWidth="1px"
              borderColor="gray.700"
              maxW="600px"
              w="full"
            >
              <CardBody p={{ base: 3, md: 4 }}>
                <VStack spacing={2}>
                  <Text
                    fontWeight="bold"
                    color="teal.200"
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {reachedCount} of {milestones.length} Milestones Reached
                  </Text>
                  <Progress
                    value={overallProgress}
                    colorScheme="green"
                    size={{ base: "sm", md: "md" }}
                    borderRadius="full"
                    w="full"
                    hasStripe
                    aria-label={`${overallProgress}% of milestones reached`}
                  />

                  {/* Value-based progress toward the $1000/mo goal */}
                  <Box w="full" pt={1}>
                    <HStack justify="space-between" mb={1}>
                      <Text
                        fontSize={{ base: "2xs", md: "xs" }}
                        color="teal.200"
                        fontWeight="medium"
                      >
                        Toward ${GOAL_AMOUNT}/mo goal
                      </Text>
                      <Text
                        fontSize={{ base: "2xs", md: "xs" }}
                        color="gray.400"
                        fontWeight="medium"
                      >
                        {goalProgress}%
                      </Text>
                    </HStack>
                    <Progress
                      value={goalProgress}
                      colorScheme="teal"
                      size={{ base: "xs", md: "sm" }}
                      borderRadius="full"
                      w="full"
                      aria-label={`${goalProgress}% toward the $${GOAL_AMOUNT} per month goal`}
                    />
                  </Box>

                  <Text fontSize={{ base: "xs", md: "sm" }} color="gray.300">
                    {motivation}
                  </Text>
                </VStack>
              </CardBody>
            </Card>

            {/* Category Filter */}
            <HStack
              spacing={{ base: 1, md: 2 }}
              flexWrap="wrap"
              justify="center"
            >
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat}
                  size={{ base: "xs", md: "sm" }}
                  variant={selectedCategory === cat ? "solid" : "outline"}
                  colorScheme={CATEGORY_COLORS[cat]}
                  onClick={() => setSelectedCategory(cat)}
                  borderRadius="full"
                  fontWeight="bold"
                  px={{ base: 2, md: 3 }}
                >
                  {cat}
                  <Badge
                    ml={1}
                    fontSize="2xs"
                    colorScheme={CATEGORY_COLORS[cat]}
                    variant={selectedCategory === cat ? "solid" : "subtle"}
                    borderRadius="full"
                  >
                    {categoryCounts[cat]}
                  </Badge>
                </Button>
              ))}
            </HStack>

            {/* Milestones Grid */}
            <Grid
              templateColumns={{
                base: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(auto-fill, minmax(140px, 1fr))",
              }}
              gap={{ base: 2, md: 3 }}
              w="full"
            >
              {filteredMilestones.map((milestone) => {
                const isNext = nextMilestone?.amount === milestone.amount;
                return (
                  <MilestoneCard
                    key={milestone.amount}
                    milestone={milestone}
                    isNext={isNext}
                    cardRef={isNext ? nextCardRef : undefined}
                    animateNext={!prefersReducedMotion}
                  />
                );
              })}
            </Grid>
          </VStack>
        </Box>
      </div>
    </Page>
  );
};

export default Milestone;